import { LeftOutlined } from "@ant-design/icons";
import { Checkbox, Col, Flex, Row } from "antd";
import RadioButton from "components/RadioButton";
import Button from "components/button/Button";
import CKEditorCustom from "components/ckeditor";
import DatePickerComponent from "components/datepicker/Datepicker";
import InputComponent from "components/formInput/Input";
import Loading from "components/loading/Loading";
import PopupConfirm from "components/popup-confirm";
import { useFormik } from "formik";
import {
  apiCreateNotice,
  apiDeleteNotice,
  apiGetNotice,
  apiUpdateNotice,
} from "helpers/api/notices";
import { isArray, keys, omit, some } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { showToastError, showToastSuccess } from "utils";
import * as yup from "yup";
import { NOTICE_PLATFORM, NOTICE_TYPE } from "../constants";
import "./styles.scss";

const NoticeForm = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [loading, setLoading] = useState(0);
  const isEdit = Boolean(id);

  const {
    values,
    errors,
    handleChange,
    setFieldValue,
    resetForm,
    validateForm,
  } = useFormik({
    validateOnChange: false,
    initialValues: {
      subject: "",
      content: "",
      content_hidden: "",
      is_public: true,
      type: NOTICE_TYPE.REGISTER,
      platform: [],
    },
    validationSchema: yup.object().shape({
      subject: yup.string().max(50, t("messages.MSG-USP052", { number: 50 })).required(),
      content: yup.string(),
      content_hidden: yup.string().max(5000, t("messages.MSG-USP052", { number: 5000 })),
      is_public: yup.boolean().required(),
      date: yup.date(),
      time: yup.date(),
      type: yup.string().required(),
      platform: yup.array(yup.string()).required(),
    }),
  });

  const isFuture = moment(values.date).isAfter(moment(), "day");

  const getNotice = () => {
    setLoading((prev) => prev + 1);
    apiGetNotice(id)
      .then(({ error, data }) => {
        if (error) showToastError(error.message);
        if (data) {
          resetForm({
            values: {
              ...data,
              date: data.release_date ? moment.utc(data.release_date).toDate() : "",
              time: data.release_date ? moment.utc(data.release_date).toDate() : "",
            },
          });
        }
      })
      .catch(({ error }) => showToastError(`messages.${error.reason}`))
      .finally(() => setLoading((prev) => prev - 1));
  };

  const createNotice = () => {
    setLoading((prev) => prev + 1);
    apiCreateNotice({
      ...omit(values, ["date", "time", "content_hidden"]),
      release_date: moment(`${moment(values.date).format("YYYY-MM-DD")} ${moment(values.time).format("HH:mm:ss")}`).utc().format(),
    })
      .then((res) => {
        if (res.error) showToastError(res.error.message);
        else {
          history.push("/admin/notices");
          showToastSuccess("messages.MSG-USP083");
        }
      })
      .catch(({ error }) => showToastError(`messages.${error.reason}`))
      .finally(() => setLoading((prev) => prev - 1));
  };

  const updateNotice = () => {
    setLoading((prev) => prev + 1);
    apiUpdateNotice({
      ...omit(values, ["date", "time", "content_hidden"]),
      release_date: moment(`${moment(values.date).format("YYYY-MM-DD")} ${moment(values.time).format("HH:mm:ss")}`).utc().format(),
    })
      .then((res) => {
        if (res.error) showToastError(res.error.message);
        else {
          history.push("/admin/notices");
          showToastSuccess("messages.MSG-USP055");
        }
      })
      .catch(({ error }) => showToastError(`messages.${error.reason}`))
      .finally(() => setLoading((prev) => prev - 1));
  };

  const deleteNotice = () => {
    setLoading((prev) => prev + 1);
    apiDeleteNotice(values.id)
      .then((res) => {
        if (res.error) showToastError(res.error.message);
        else {
          history.push("/admin/notices");
          showToastSuccess("messages.MSG-USP045");
        }
      })
      .catch(({ error }) => showToastError(`messages.${error.reason}`))
      .finally(() => setLoading((prev) => prev - 1));
  };

  const handleNotice = async () => {
    const isValid = Boolean(!keys(await validateForm()).length);
    if (isValid) {
      !isEdit ? createNotice() : updateNotice();
    }
  };

  useEffect(() => {
    if (id) getNotice();
  }, [id]);

  useEffect(() => {
    setFieldValue("content_hidden", document.getElementById(`ckeditor-${id}`)?.innerText);
  }, [values.content]);

  return (
    <div className="notice-create-container main-content">
      <div className="root-right-header">
        <div className="screen-name">{t("admin_role")}</div>
      </div>

      <div className="nav">
        <Flex gap="large" wrap="wrap" align="center">
          <div className="screen-title" onClick={() => history.push("/admin/notices")}> <LeftOutlined />
            {t(`notices.form.${!isEdit ? "createTitle" : "editTitle"}`)}
          </div>
        </Flex>

        <Row className="notice-form" gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <div className="notice-input">
              <div className="input-title">{t("notices.form.title")}</div>
              <InputComponent
                style={{ border: "none" }}
                placeholder={t("notices.form.title")}
                name="subject"
                value={values?.subject}
                error={errors?.subject}
                onChange={handleChange}
              />
            </div>

            <div style={{ marginTop: "10px" }}>
              <div className="input-title">{t("notices.form.content")}</div>
              <CKEditorCustom
                showToolbar={false}
                name="content"
                placeholder={t("notices.form.content")}
                value={values?.content}
                error={errors?.content_hidden}
                onChange={(_, editor) => {
                  setFieldValue("content", editor.getData());
                  editor.ui.getEditableElement().id = `ckeditor-${id}`;
                }}
              />
            </div>
          </Col>

          <Col xs={24} md={8}>
            <div className="public-session">
              <RadioButton
                title={t("notices.form.publicPrivate")}
                options={[
                  { title: t("notices.form.public"), value: true },
                  { title: t("notices.form.private"), value: false },
                ]}
                name="is_public"
                value={values?.is_public}
                onChange={handleChange}
              />

              <div className="time-section">
                <div className="time-section-title">{t(`notices.form.publicDate`)}</div>
                <DatePickerComponent
                  prefix="calendar-gray.png"
                  placeholder="YYYY/MM/DD"
                  format="YYYY/MM/DD"
                  name="date"
                  value={values?.date}
                  error={errors?.date}
                  onChange={(e) => setFieldValue("date", e)}
                />
                <DatePickerComponent
                  prefix="clock-gray.png"
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={1}
                  placeholder="HH:MM"
                  dateFormat="HH:mm"
                  name="time"
                  value={values?.time}
                  error={errors?.time}
                  onChange={(e) => setFieldValue("time", e)}
                />
              </div>
            </div>

            <div className="public-session">
              <RadioButton
                title={t("notices.form.condition")}
                options={[
                  { title: t("notices.form.whenRegistration"), value: NOTICE_TYPE.REGISTER },
                  { title: t("notices.form.thisTime"), value: NOTICE_TYPE.SPECIAL_TIME },
                  { title: t("notices.form.appUpdate"), value: NOTICE_TYPE.APP_NEW_VERSION },
                ]}
                name="type"
                value={values?.type}
                onChange={handleChange}
              />

              <div style={{ fontWeight: "bold", marginTop: 12 }}>{t("notices.form.destination")}</div>
              <Checkbox.Group
                name="platform"
                value={values?.platform}
                options={[
                  { label: t("notices.form.clinicViewer"), value: NOTICE_PLATFORM.WEB },
                  { label: t("notices.form.app"), value: NOTICE_PLATFORM.APP },
                ]}
                onChange={(checkedValues) => setFieldValue("platform", checkedValues)}
              />
            </div>
          </Col>
        </Row>

        <Row className="help-button" gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col xs={24} md={8}>
            <Button
              text={t("notices.form.save")}
              width="100%"
              height={40}
              type="button"
              style={{ fontSize: 20, fontWeight: "bold", borderRadius: 60 }}
              className="action-btn save-button"
              onClick={() => handleNotice()}
              disabled={some(Object.values(omit(values, ["date", "time"])), (item) => (!item && item !== false) || (isArray(item) && !item.length))}
            />
          </Col>

          <Col xs={24} md={12}>
            <Button
              text={t("notices.form.deleteArticle")}
              width="100%"
              height={40}
              background="#FF3131"
              color="#FFFFFF"
              onClick={() => setIsOpenDelete(true)}
              type="button"
              disabled={!isEdit}
              style={{ fontSize: 20, fontWeight: "bold", borderRadius: 60 }}
              className="action-btn"
            />
          </Col>
        </Row>
      </div>

      <PopupConfirm
        open={isOpenDelete}
        onCancelText={t("button.cancel")}
        onOkText={t("button.delete")}
        content={t("confirm_delete")}
        onOk={() => deleteNotice()}
        onCancel={() => setIsOpenDelete(false)}
        onOkColor="#FF3131"
      />

      {loading ? <Loading /> : null}
    </div>
  );
};

export default NoticeForm;